import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { CaseStudyGrid } from './components/CaseStudyGrid';
import { MediaSection } from './components/MediaSection';
import { VideoModal } from './components/VideoModal';
import { Credentials } from './components/Credentials';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { AdminLogin } from './admin/AdminLogin';
import { AdminDashboard } from './admin/AdminDashboard';
import { CaseStudy, PerformanceRecording, Credential } from './types';

const INITIAL_CASE_STUDIES: CaseStudy[] = [
  {
    id: 'internal-calendar-tooling',
    discipline_id: 'cloud',
    title: 'Internal Calendar Management Tool',
    subtitle: 'Calendar Productivity & Automation Tooling',
    category: 'Software Engineering',
    summary: 'Developed an internal calendar management tool across Alphabet, scaling adoption from 85 to over 2,300 active engineers and syncing 42,000+ calendars.',
    impact: [
      'Scaled active adoption across engineering teams from 85 to 2,300+ users (+2,600%)',
      'Automated calendar workflows managing 42,000+ schedules and events'
    ],
    tech_stack: ['Go', 'TypeScript', 'Google Cloud', 'Automation'],
    featured: true
  },
  {
    id: 'enterprise-security-governance',
    discipline_id: 'cloud',
    title: 'Enterprise Security, AI Security & Zero-Trust',
    subtitle: 'Zero-Trust Architecture & Policy-as-Code',
    category: 'Security Architecture',
    summary: 'Designed and implemented secure cloud architecture foundations for major enterprise clients in Finance, E-Commerce, and Healthcare.',
    impact: [
      'Developed reusable cloud governance framework adopted by Google\'s Office of the CISO',
      'Engineered automated security posture and comprehension tooling across enterprise fleets',
      'Designed least-privileged IAM, VPC Service Controls, and PKI infrastructure'
    ],
    tech_stack: ['Terraform', 'Rego (OPA)', 'Google Cloud Security', 'Zero-Trust', 'VPC-SC', 'IAM'],
    featured: true,
    published: true
  },
  {
    id: 'cel-open-source',
    discipline_id: 'cloud',
    title: 'Common Expression Language (CEL) Contributions',
    subtitle: 'Open-Source IP & CIDR Address Parsing',
    category: 'Open Source',
    summary: 'Authored open-source extensions to Google\'s Common Expression Language (CEL) in Go to enable native IP address and CIDR parsing in security policies.',
    impact: [
      'Extends CEL policy evaluation engines in Go, Java, and C++',
      'Directly applicable to Custom Organization Policies and Conditional IAM expressions'
    ],
    tech_stack: ['Go', 'CEL', 'Open Source', 'Security Policies'],
    external_links: { 'GitHub PR': 'https://github.com/google/cel-go/pull/1238' },
    featured: true
  },
  {
    id: 'classical-remix-production',
    discipline_id: 'music',
    title: 'Classical Remix Festival Operations',
    subtitle: 'Live Concert & Festival Event Production',
    category: 'Arts Administration & Operations',
    summary: 'Directing on-site event logistics, stage operations, and volunteer coordination for Classical Remix Music Festival in Atlanta.',
    impact: [
      'Coordinated venue logistics, artist hospitality, and on-site concert operations for festival events',
      'Managed volunteer teams, stage coordination, and audience check-in workflows'
    ],
    tech_stack: ['Event Production', 'Arts Administration', 'Operations Management', 'Volunteer Leadership'],
    external_links: { 'Festival Website': 'https://classicalremixmusicfestival.org' },
    featured: true,
    published: true
  },
  {
    id: 'jukebox-party',
    discipline_id: 'maker',
    title: 'Jukebox Party: Real-Time Concert Tech',
    subtitle: 'Cloud-Native Live Audience Request Platform',
    category: 'Live Event Systems',
    summary: 'A real-time, synchronized festival platform connecting guest iPad kiosks, a stage projector display, and a mobile director remote for live concert song requests.',
    impact: [
      'Architected real-time state synchronization using Google Cloud Firestore and Go Gin API',
      'Built interactive guest UI with React, Framer Motion, and Stripe payment tokenization',
      'Designed physical credit/ticket kiosk mode for seamless in-venue token redemption'
    ],
    tech_stack: ['Go (Gin)', 'React', 'Google Cloud Firestore', 'Stripe API', 'Cloud Run', 'Terraform'],
    external_links: { 'Source Code': 'https://github.com/tdesrosi/jukebox-party' },
    featured: true
  },
  {
    id: 'custom-hifi-amplifier',
    discipline_id: 'maker',
    title: 'Custom 1000W Hi-Fi Audio Amplifier',
    subtitle: 'First-Principles Analog Audio Engineering',
    category: 'Hardware & Audio Electronics',
    summary: 'Designed and built a dual-channel 150W/channel Class-AB high-fidelity audio amplifier encased in custom acrylic and wood casework with modern Bluetooth and protection circuitry.',
    impact: [
      'Integrated circuit overload protection, active thermal management, and hidden volume-limit potentiometers',
      'Featured at the 2019 Nashville Maker Faire at Vanderbilt University'
    ],
    tech_stack: ['Analog Circuit Design', 'Eagle CAD', 'Class-AB Amplification', 'Thermal Engineering', 'Casework'],
    featured: true
  },
  {
    id: 'makerspace-electronics-workshops',
    discipline_id: 'maker',
    title: 'Community Electronics & Audio Workshops',
    subtitle: 'Hands-On Analog Circuit Education',
    category: 'Education & Making',
    summary: 'Led hands-on analog circuit and audio amplification workshops for students and community makers at Vanderbilt University\'s Wond\'ry Innovation Hub.',
    impact: [
      'Designed custom PCB amplifier kits based on the LM386 op-amp in Autodesk Eagle CAD',
      'Taught soldering, circuit analysis, and audio signal fundamentals to diverse multidisciplinary attendees'
    ],
    tech_stack: ['Circuit Design', 'Eagle CAD', 'STEM Education', 'PCB Prototyping'],
    featured: true
  }
];

const INITIAL_RECORDINGS: PerformanceRecording[] = [
  {
    id: 'brahms-quintet-mvt2',
    title: 'Johannes Brahms — Clarinet Quintet in B minor, Op. 115 (Mvt. II)',
    composer: 'Johannes Brahms (1833–1897)',
    piece: 'Clarinet Quintet in B minor, Op. 115',
    movement: 'II. Adagio',
    instrumentation: 'Clarinet and String Quartet',
    event: 'A Return to Performing: Clarinet Recital (Benefiting Classical Remix Music Festival)',
    date: 'March 7, 2026',
    venue: 'Unitarian Universalist Congregation of Atlanta',
    youtube_url: 'https://youtu.be/DLX7vikFCcE',
    youtube_id: 'DLX7vikFCcE',
    performers: [
      {
        name: 'Thomas Desrosiers',
        instrument: 'Clarinet',
        affiliation: 'Classical Remix / Blair School of Music',
        url: 'https://tdesrosi.com'
      },
      {
        name: 'Kevin Chen',
        instrument: 'Violin',
        affiliation: 'Atlanta Symphony Orchestra',
        url: 'https://www.aso.org/artists/detail/kevin-chen'
      },
      {
        name: 'Alice Hong',
        instrument: 'Violin',
        affiliation: 'Violinist & Composer / CBC 30 Under 30',
        url: 'https://www.aliceyhong.com/'
      },
      {
        name: 'Joseph Skerik',
        instrument: 'Viola',
        affiliation: 'Vega String Quartet (Emory University)',
        url: 'https://music.emory.edu/people/biography/Skerik-Joseph.html'
      },
      {
        name: 'Nathan Mo',
        instrument: 'Cello',
        affiliation: 'Atlanta Symphony Orchestra / Soloist',
        url: 'https://www.aso.org/artists/detail/nathan-mo'
      }
    ],
    description: 'Johannes Brahms: Clarinet Quintet in B minor, Op. 115 — II. Adagio. Performed live in Atlanta, GA.'
  },
  {
    id: 'poulenc-sonata-mvt3',
    title: 'Francis Poulenc — Clarinet Sonata, FP 184 (Mvt. III)',
    composer: 'Francis Poulenc (1899–1963)',
    piece: 'Clarinet Sonata, FP 184',
    movement: 'III. Allegro con fuoco (Très animé)',
    instrumentation: 'Clarinet and Piano',
    event: 'A Return to Performing: Clarinet Recital (Benefiting Classical Remix Music Festival)',
    date: 'March 7, 2026',
    venue: 'Unitarian Universalist Congregation of Atlanta',
    youtube_url: 'https://youtu.be/a1GcInMCL_k',
    youtube_id: 'a1GcInMCL_k',
    performers: [
      {
        name: 'Thomas Desrosiers',
        instrument: 'Clarinet',
        affiliation: 'Classical Remix / Blair School of Music',
        url: 'https://tdesrosi.com'
      },
      {
        name: 'Nicole Ying',
        instrument: 'Piano',
        affiliation: 'Collaborative Pianist',
        url: 'https://www.nicoleying.com/'
      }
    ],
    description: 'Francis Poulenc: Clarinet Sonata, FP 184 — III. Allegro con fuoco (Très animé). Performed live in Atlanta, GA.'
  },
  {
    id: 'bach-chaconne-langenus',
    title: 'J.S. Bach — Chaconne from Partita No. 2 in D minor, BWV 1004 (arr. Gustave Langenus)',
    composer: 'Johann Sebastian Bach (1685–1750)',
    piece: 'Chaconne from Partita No. 2 in D minor, BWV 1004',
    movement: 'Arranged for Solo Clarinet by Gustave Langenus',
    instrumentation: 'Solo Clarinet',
    event: 'A Return to Performing: Clarinet Recital (Benefiting Classical Remix Music Festival)',
    date: 'March 7, 2026',
    venue: 'Unitarian Universalist Congregation of Atlanta',
    youtube_url: 'https://youtu.be/yFQ2JVDWBjQ',
    youtube_id: 'yFQ2JVDWBjQ',
    performers: [
      {
        name: 'Thomas Desrosiers',
        instrument: 'Clarinet',
        affiliation: 'Classical Remix / Blair School of Music',
        url: 'https://tdesrosi.com'
      }
    ],
    description: 'J.S. Bach: Chaconne from Partita No. 2 in D minor, BWV 1004 (arr. Gustave Langenus). Performed live in Atlanta, GA.'
  },
  {
    id: 'youngeltro-will-you-still-sing-my-songs',
    title: 'YoungEltro — WILL YOU STILL SING MY SONGS? (feat. Thomas Desrosiers)',
    composer: 'YoungEltro',
    piece: 'WILL YOU STILL SING MY SONGS?',
    movement: 'MASQUE : ACT II (Track 1)',
    instrumentation: 'Tenor Saxophone, Vocals, Electronic Production',
    event: 'Studio Album & Commercial Release',
    date: '2026',
    venue: 'Studio Release',
    youtube_url: 'https://youtu.be/T2DGvJ1GfN8',
    youtube_id: 'T2DGvJ1GfN8',
    category: 'Studio Recording',
    streaming_links: {
      'Spotify': 'https://open.spotify.com/track/2zi56kWmhW7rqDFVIsHhZI',
      'Apple Music': 'https://music.apple.com/album/masque-act-ii/1836173264?i=1836173273',
      'YouTube': 'https://youtu.be/T2DGvJ1GfN8'
    },
    performers: [
      {
        name: 'Thomas Desrosiers',
        instrument: 'Tenor Saxophone',
        affiliation: 'Blair School of Music',
        url: 'https://tdesrosi.com'
      },
      {
        name: 'YoungEltro',
        instrument: 'Vocals & Production',
        affiliation: 'Artist / Producer',
        url: 'https://music.apple.com/artist/youngeltro/1676646549'
      }
    ],
    description: 'YoungEltro: WILL YOU STILL SING MY SONGS? featuring Thomas Desrosiers on tenor saxophone from the album MASQUE : ACT II.'
  }
];

const INITIAL_CREDENTIALS: Credential[] = [
  {
    title: 'Strategic Cloud Engineer',
    institution: 'Google Cloud',
    period: '2022 – Present',
    details: 'Enterprise Security, AI Security, Zero-Trust Architecture, and Developer Platform Lead.',
    category: 'experience'
  },
  {
    title: 'Cloud Technical Resident',
    institution: 'Google Cloud',
    period: '2021 – 2022',
    details: 'Enterprise cloud architectures and customer technical enablement across Google Cloud.',
    category: 'experience'
  },
  {
    title: 'Director of Operations & Production',
    institution: 'Classical Remix Music Festival',
    period: '2025 – Present',
    details: 'Operational leadership, festival production, ticketing systems, and digital infrastructure.',
    category: 'experience'
  },
  {
    title: 'Master of Marketing (Digital Analytics)',
    institution: 'Vanderbilt University (Owen Graduate School of Management)',
    period: '2020 – 2021',
    details: 'Specialized in digital marketing strategy, conversion optimization, and web analytics.',
    category: 'education'
  },
  {
    title: 'Bachelor of Musical Arts (Clarinet Performance & Business)',
    institution: 'Vanderbilt University (Blair School of Music)',
    period: '2016 – 2020',
    details: 'Cornelius Vanderbilt Scholar. Clarinet performance major under Bil Jackson with business minor.',
    category: 'education'
  },
  {
    title: 'Professional Cloud Architect',
    institution: 'Google Cloud',
    period: 'Active',
    details: 'Enterprise architecture, multi-region resilience, and cloud migration.',
    category: 'certification'
  },
  {
    title: 'Professional Cloud Security Engineer',
    institution: 'Google Cloud',
    period: 'Active',
    details: 'Identity, zero-trust network boundaries, compliance, and encryption.',
    category: 'certification'
  },
  {
    title: 'Professional Cloud Security Operations Engineer',
    institution: 'Google Cloud',
    period: 'Active',
    details: 'Incident detection, security monitoring, and automated response.',
    category: 'certification'
  },
  {
    title: 'Zodiac Music Academy & Festival',
    institution: 'Valdeblore, France',
    period: '2026',
    details: 'Chamber music and solo clarinet masterclasses with international faculty.',
    category: 'award'
  },
  {
    title: 'Google Cloud Club Award Winner',
    institution: 'Google LLC',
    period: '2024',
    details: 'Awarded for company-wide impact, engineering excellence, and internal tooling leadership.',
    category: 'award'
  }
];

export const App: React.FC = () => {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [activeDiscipline, setActiveDiscipline] = useState<string | null>(null);
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>(INITIAL_CASE_STUDIES);
  const [recordings, setRecordings] = useState<PerformanceRecording[]>(INITIAL_RECORDINGS);
  const [credentials, setCredentials] = useState<Credential[]>(INITIAL_CREDENTIALS);
  const [selectedRecording, setSelectedRecording] = useState<PerformanceRecording | null>(null);

  const [isAdminView, setIsAdminView] = useState<boolean>(
    window.location.hash === '#admin' || window.location.pathname.startsWith('/admin')
  );
  const [adminToken, setAdminToken] = useState<string | null>(() => localStorage.getItem('tdesrosi_admin_token'));
  const [adminUser, setAdminUser] = useState<{ email: string; name: string } | null>(() => {
    const saved = localStorage.getItem('tdesrosi_admin_user');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    const handleHashChange = () => {
      setIsAdminView(window.location.hash === '#admin' || window.location.pathname.startsWith('/admin'));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const fetchData = async () => {
    try {
      const [csRes, recRes, credRes] = await Promise.allSettled([
        fetch('/api/case-studies').then((r) => r.ok ? r.json() : null),
        fetch('/api/media').then((r) => r.ok ? r.json() : null),
        fetch('/api/credentials').then((r) => r.ok ? r.json() : null),
      ]);

      if (csRes.status === 'fulfilled' && csRes.value) setCaseStudies(csRes.value);
      if (recRes.status === 'fulfilled' && recRes.value) setRecordings(recRes.value);
      if (credRes.status === 'fulfilled' && credRes.value) setCredentials(credRes.value);
    } catch (err) {
      console.warn('Using local fallback state:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const handleLoginSuccess = (token: string, user: { email: string; name: string }) => {
    setAdminToken(token);
    setAdminUser(user);
    localStorage.setItem('tdesrosi_admin_token', token);
    localStorage.setItem('tdesrosi_admin_user', JSON.stringify(user));
  };

  const handleLogout = () => {
    setAdminToken(null);
    setAdminUser(null);
    localStorage.removeItem('tdesrosi_admin_token');
    localStorage.removeItem('tdesrosi_admin_user');
    window.location.hash = '';
    setIsAdminView(false);
  };

  const handleBackToLiveSite = () => {
    window.location.hash = '';
    setIsAdminView(false);
    fetchData(); // Refresh public site with latest data
  };

  // If in admin view
  if (isAdminView) {
    if (adminToken && adminUser) {
      return (
        <AdminDashboard
          token={adminToken}
          adminUser={adminUser}
          onLogout={handleLogout}
          onViewLiveSite={handleBackToLiveSite}
          initialRecordings={recordings}
          initialCaseStudies={caseStudies}
          initialCredentials={credentials}
        />
      );
    }
    return (
      <AdminLogin
        onLoginSuccess={handleLoginSuccess}
        onBackToSite={handleBackToLiveSite}
      />
    );
  }

  return (
    <div className="app-root">
      <div className="bg-ambience" />
      <div className="content-wrapper">
        <Navbar theme={theme} onToggleTheme={toggleTheme} />
        <main>
          <Hero />
          <CaseStudyGrid
            caseStudies={caseStudies}
            activeDiscipline={activeDiscipline}
            onFilterChange={setActiveDiscipline}
          />
          <MediaSection
            recordings={recordings}
            onSelectRecording={setSelectedRecording}
          />
          <Credentials credentials={credentials} />
          <ContactSection />
        </main>
        <Footer onOpenAdmin={() => setIsAdminView(true)} />
      </div>

      <VideoModal
        recording={selectedRecording}
        onClose={() => setSelectedRecording(null)}
      />
    </div>
  );
};

export default App;
